/* /biometric/unmatched — device users not linked to an employee + existing links. */
import { useMemo, useState } from "react";
import type { DeviceUserMap, UnmatchedUser } from "../../data/types";
import { useAllEmployees, useDevices, useMappings, useUnmapUser, useUnmatchedUsers } from "../../data/hooks";
import { AiNote, Button, Card, Confirm, EmptyState, Loaded, PageHeader, Person, SearchBox, errMsg, useToast } from "../../ui";
import { useI18n } from "../../i18n";
import { LinkEmployeeModal } from "./components/LinkEmployeeModal";
import { hm } from "./components/labels";
import "./time.css";

const SHOW = 15;

export default function Unmatched() {
  const i = useI18n();
  const { L, t } = i;
  const toast = useToast();
  const um = useUnmatchedUsers();
  const maps = useMappings();
  const devs = useDevices();
  const emps = useAllEmployees();
  const unmap = useUnmapUser();
  const [link, setLink] = useState<UnmatchedUser | null>(null);
  const [q, setQ] = useState("");
  const [more, setMore] = useState(false);
  const [del, setDel] = useState<DeviceUserMap | null>(null);
  const devName = (id: string) => (id === "*" ? L("All devices", "كل الأجهزة") : devs.data?.find((d) => d.id === id)?.name ?? id);

  const filteredMaps = useMemo(() => {
    const s = q.trim().toLowerCase();
    // Device-specific links (exceptions) first, then "all devices" links by user ID.
    const sorted = (maps.data ?? []).slice().sort((a, b) => Number(a.deviceId === "*") - Number(b.deviceId === "*") || a.deviceUserId.localeCompare(b.deviceUserId, "en", { numeric: true }));
    return sorted.filter((m) => {
      if (!s) return true;
      const e = emps.data?.find((x) => x.id === m.employeeId);
      return m.deviceUserId.toLowerCase().includes(s) || (!!e && (e.nameEn.toLowerCase().includes(s) || e.nameAr.includes(q.trim()) || e.code.toLowerCase().includes(s)));
    });
  }, [maps.data, emps.data, q]);

  const doUnmap = () => {
    if (!del) return;
    unmap.mutate({ deviceId: del.deviceId, deviceUserId: del.deviceUserId }, {
      onSuccess: () => { toast(L("Link removed — new punches from this user will be unmatched", "تم إلغاء الربط — ستظهر البصمات الجديدة لهذا المستخدم كغير مرتبطة")); setDel(null); },
      onError: (e) => toast(errMsg(e), "bad"),
    });
  };

  return (
    <div data-testid="unmatched-page">
      <PageHeader back={{ to: "/biometric", label: L("Biometric devices", "أجهزة البصمة") }} title={L("Unmatched device users", "مستخدمو الأجهزة غير المرتبطين")}
        sub={L("Punches from user IDs that aren't linked to an employee. Link them once — past and future punches are fixed automatically.", "بصمات من أرقام مستخدمين غير مرتبطة بموظف. اربطها مرة واحدة — تُصحَّح البصمات السابقة واللاحقة تلقائيًا.")} />

      <Card title={L("Waiting to be linked", "بانتظار الربط")} flush testId="unmatched-card">
        <Loaded q={um}>{(list) => list.length === 0 ? (
          <EmptyState title={L("All device users are linked", "كل مستخدمي الأجهزة مرتبطون")} desc={L("New user IDs appear here as soon as they punch.", "تظهر أرقام المستخدمين الجديدة هنا فور البصم.")} />
        ) : (
          <div className="tw"><table className="tbl cards" data-testid="unmatched-table">
            <thead><tr><th>{L("Device user", "مستخدم الجهاز")}</th><th>{L("Device", "الجهاز")}</th><th className="end">{L("Punches", "البصمات")}</th><th className="hide-md">{L("First seen", "أول ظهور")}</th><th className="hide-md">{L("Last seen", "آخر ظهور")}</th><th className="end">{t("Actions")}</th></tr></thead>
            <tbody>{list.map((u) => (
              <tr key={u.deviceId + "|" + u.deviceUserId} data-testid={`um-${u.deviceId}-${u.deviceUserId}`}>
                <td className="k1"><b className="num" dir="ltr">{u.deviceUserId}</b><div className="xs muted">{u.name || L("No name on device", "بدون اسم في الجهاز")}</div></td>
                <td className="x" data-l={L("Device", "الجهاز")}>{devName(u.deviceId)}</td>
                <td className="end k2 num"><b>{u.punches}</b></td>
                <td className="hide-md num sm">{u.firstSeen.slice(0, 10)} {hm(u.firstSeen)}</td>
                <td className="hide-md num sm">{u.lastSeen.slice(0, 10)} {hm(u.lastSeen)}</td>
                <td className="end k3" data-l=""><Button variant="pri sm" icon="link" onClick={() => setLink(u)} data-testid={`btn-link-${u.deviceId}-${u.deviceUserId}`}>{L("Link to employee", "ربط بموظف")}</Button></td>
              </tr>
            ))}</tbody>
          </table></div>
        )}</Loaded>
        <div className="card-b"><AiNote>{L("Tip: enrol people on the device with their StartHR employee number as the user ID — they're linked automatically.", "نصيحة: سجّل الموظفين في الجهاز برقمهم الوظيفي في ستارت HR كرقم مستخدم — وسيتم ربطهم تلقائيًا.")}</AiNote></div>
      </Card>

      <div className="mt">
        <Card title={L("Linked device users", "مستخدمو الأجهزة المرتبطون")} sub={maps.data ? L(`${maps.data.length} links`, `${maps.data.length} رابط`) : undefined} flush testId="mappings-card">
          <div className="toolbar"><SearchBox value={q} onChange={setQ} placeholder={L("Search user ID or employee…", "ابحث برقم المستخدم أو الموظف…")} testId="map-search" /></div>
          <Loaded q={maps}>{() => filteredMaps.length === 0 ? <EmptyState title={L("No links match", "لا توجد روابط مطابقة")} /> : (
            <>
              <div className="tw"><table className="tbl cards compact" data-testid="mappings-table">
                <thead><tr><th>{L("Device user", "مستخدم الجهاز")}</th><th>{t("Employee")}</th><th>{L("Device", "الجهاز")}</th><th className="end">{t("Actions")}</th></tr></thead>
                <tbody>{(more ? filteredMaps : filteredMaps.slice(0, SHOW)).map((m) => {
                  const e = emps.data?.find((x) => x.id === m.employeeId);
                  return (
                    <tr key={m.deviceId + "|" + m.deviceUserId} data-testid={`map-${m.deviceId}-${m.deviceUserId}`}>
                      <td className="k3 num" data-l={L("Device user", "مستخدم الجهاز")} dir="ltr">{m.deviceUserId}</td>
                      <td className="k1">{e ? <Person e={e} /> : "#" + m.employeeId}</td>
                      <td className="x" data-l={L("Device", "الجهاز")}>{devName(m.deviceId)}</td>
                      <td className="end k2"><Button variant="ghost sm" onClick={() => setDel(m)} data-testid={`btn-unmap-${m.deviceId}-${m.deviceUserId}`}>{L("Unlink", "إلغاء الربط")}</Button></td>
                    </tr>
                  );
                })}</tbody>
              </table></div>
              {filteredMaps.length > SHOW && <div className="card-b"><Button variant="ghost sm" onClick={() => setMore((x) => !x)} data-testid="btn-more">{more ? L("Show less", "عرض أقل") : L(`Show all ${filteredMaps.length}`, `عرض الكل (${filteredMaps.length})`)}</Button></div>}
            </>
          )}</Loaded>
        </Card>
      </div>

      {link && <LinkEmployeeModal user={link} deviceName={devName(link.deviceId)} onClose={() => setLink(null)} />}
      {del && <Confirm title={L("Unlink this device user?", "إلغاء ربط مستخدم الجهاز؟")} confirmLabel={L("Unlink", "إلغاء الربط")} danger busy={unmap.isPending} onClose={() => setDel(null)} onConfirm={doUnmap}
        body={L("Punches already linked keep their employee. New punches from this user ID will go to the Unmatched inbox.", "تبقى البصمات المرتبطة سابقًا كما هي. البصمات الجديدة لهذا الرقم ستذهب إلى صندوق غير المرتبطين.")} />}
    </div>
  );
}
